import { useEffect, useRef } from "react";
import {
  createChart,
  IChartApi,
  ISeriesApi,
  Time,
  CrosshairMode,
  ColorType,
  LineStyle,
  CandlestickSeries,
  createSeriesMarkers,
} from "lightweight-charts";
import { useGetGridSymbolChartQuery } from "@/store/api";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * GridSymbolChart — TradingView lightweight-charts candlestick chart
 * per grid symbol. Overlays:
 *   - Horizontal price lines at every grid level
 *       (green = open buy, red = open sell, grey-dashed = passive)
 *   - Live price as a yellow dashed line with axis label
 *   - Up/down markers at every fill (▲ green for buys, ▼ red for sells)
 *
 * Chart auto-resizes via `autoSize: true`. Data polls every 30s.
 */
export function GridSymbolChart({ base, height = 320 }: { base: string; height?: number }) {
  const { data, isLoading, error } = useGetGridSymbolChartQuery(
    { base, timeframe: "4h", limit: 120 },
    { pollingInterval: 30_000 },
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const priceLinesRef = useRef<any[]>([]);
  const markersRef = useRef<any>(null);

  // Mount chart once. Don't use autoSize — when the panel mounts already
  // expanded, autoSize's internal ResizeObserver hasn't settled yet by
  // the time setData() runs, so the chart paints at 0×0 and stays blank
  // until the user toggles the panel (which forces a remount with a
  // settled container). Explicit width/height + our own ResizeObserver
  // makes the first paint reliable.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const w = el.clientWidth || el.parentElement?.clientWidth || 800;
    const h = el.clientHeight || height;
    const chart = createChart(el, {
      width: w,
      height: h,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#9aa0a6",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.04)" },
        horzLines: { color: "rgba(255,255,255,0.04)" },
      },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.1)" },
      timeScale: { borderColor: "rgba(255,255,255,0.1)", timeVisible: true, secondsVisible: false },
      crosshair: { mode: CrosshairMode.Normal },
    });
    const candles = chart.addSeries(CandlestickSeries, {
      upColor: "rgb(63, 195, 128)",
      downColor: "rgb(232, 65, 66)",
      borderUpColor: "rgb(63, 195, 128)",
      borderDownColor: "rgb(232, 65, 66)",
      wickUpColor: "rgb(63, 195, 128)",
      wickDownColor: "rgb(232, 65, 66)",
    });
    chartRef.current = chart;
    candleRef.current = candles;

    // Handle parent-driven resizes (window resize, sibling collapses, etc.)
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cw = entry.contentRect.width;
        const ch = entry.contentRect.height || height;
        if (cw > 0) {
          try { chart.resize(cw, ch); } catch {}
        }
      }
    });
    ro.observe(el);
    // Also re-measure on next animation frame — covers the case where
    // initial render measured 0 (panel content not yet laid out).
    const raf = requestAnimationFrame(() => {
      const cw = el.clientWidth;
      const ch = el.clientHeight || height;
      if (cw > 0) {
        try { chart.resize(cw, ch); } catch {}
      }
    });

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
      priceLinesRef.current = [];
      markersRef.current = null;
    };
  }, [height]);

  // Push data whenever the query updates
  useEffect(() => {
    if (!data || !candleRef.current) return;
    const bars: number[][] = data.bars || [];
    if (bars.length < 2) return;
    const candleData = bars.map((b) => ({
      time: Math.floor(Number(b[0]) / 1000) as Time,
      open: Number(b[1]),
      high: Number(b[2]),
      low: Number(b[3]),
      close: Number(b[4]),
    }));
    candleRef.current.setData(candleData);

    // Replace price lines (grid levels + live price)
    for (const pl of priceLinesRef.current) {
      try { candleRef.current.removePriceLine(pl); } catch {}
    }
    priceLinesRef.current = [];

    const openOrders: any[] = data.open_orders || [];
    const buyPrices = new Set(openOrders.filter((o) => o.side === "buy").map((o) => Number(o.price)));
    const sellPrices = new Set(openOrders.filter((o) => o.side === "sell").map((o) => Number(o.price)));
    const gridPrices: number[] = data.grid_prices || [];
    for (const p of gridPrices) {
      const isBuy = buyPrices.has(p);
      const isSell = sellPrices.has(p);
      const pl = candleRef.current.createPriceLine({
        price: p,
        color: isBuy ? "rgba(63,195,128,0.65)"
              : isSell ? "rgba(232,65,66,0.65)"
              : "rgba(140,140,140,0.20)",
        lineWidth: isBuy || isSell ? 1 : 1,
        lineStyle: isBuy || isSell ? LineStyle.Solid : LineStyle.Dotted,
        axisLabelVisible: isBuy || isSell,
        title: isBuy ? "BUY" : isSell ? "SELL" : "",
      });
      priceLinesRef.current.push(pl);
    }
    if (data.live_price) {
      const pl = candleRef.current.createPriceLine({
        price: Number(data.live_price),
        color: "#facc15",
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: "LIVE",
      });
      priceLinesRef.current.push(pl);
    }

    // Fill markers — ▲ green for buys, ▼ red for sells. createSeriesMarkers
    // is the v5 API (replaces the deprecated series.setMarkers).
    const fills: any[] = data.fills || [];
    const markers = fills.map((f) => ({
      time: Math.floor(new Date(f.ts).getTime() / 1000) as Time,
      position: f.side === "buy" ? "belowBar" : "aboveBar" as any,
      color: f.side === "buy" ? "#22c55e" : "#ef4444",
      shape: f.side === "buy" ? "arrowUp" : "arrowDown" as any,
      text: f.pnl_usd != null && Number(f.pnl_usd) !== 0
        ? `$${Number(f.pnl_usd).toFixed(2)}` : "",
    }));
    if (markersRef.current) {
      try { markersRef.current.setMarkers(markers); }
      catch { markersRef.current = null; }
    }
    if (!markersRef.current && candleRef.current) {
      markersRef.current = createSeriesMarkers(candleRef.current, markers);
    }

    chartRef.current?.timeScale().fitContent();
  }, [data]);

  // Keep the container ALWAYS mounted (even while loading) so the chart's
  // mount useEffect has a real DOM node + measured width from the first
  // render. Otherwise the container only appears after data arrives, and
  // the chart's autoSize / ResizeObserver race the data-arrival render —
  // result: blank chart until the user toggles the panel (which forces
  // a clean remount with a settled container).
  const showError = !!(error || (data && data.error));
  const showInsufficient = !!(data && (data.bars || []).length < 2 && !showError && !isLoading);
  return (
    <div className="relative" style={{ width: "100%", height }}>
      <div ref={containerRef} style={{ width: "100%", height }} />
      {isLoading && (
        <div className="absolute inset-0">
          <Skeleton className="w-full h-full" />
        </div>
      )}
      {showError && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-muted bg-bg/80">
          Chart unavailable ({data?.error ?? "fetch error"}).
        </div>
      )}
      {showInsufficient && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-muted bg-bg/80">
          Insufficient bars for chart.
        </div>
      )}
    </div>
  );
}
